import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Controller, ControllerUser, CurrentInfoService, MilkControlSummary, Producer, ProducerSummary } from '@homeleite/domain';
import { ControllersApiRepository } from '../../../../../../../libs/domain/src/lib/controllers/controllers-api.repository';
import {
    ControllerControlRow,
    MilkControlsApiRepository
} from '../../../../../../../libs/domain/src/lib/milk-control/milk-controls-api.repository';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    firstValueFrom,
    map,
    Observable,
    Subject
} from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, takeUntil, timeout } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
    CONTROL_DRAWER_CONFIG,
    ProducerDetailsMilkControlAnimalsDialogComponent
} from '../../producers/producer-details/producer-details-milk-controls/producer-details-milk-control-animals-dialog.component';

export type ControllerView = 'painel' | 'vincular' | 'historico';

@Component({
    selector: 'homeleite-controller-home',
    templateUrl: './controller-home.component.html',
    styleUrls: ['./controller-home.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ControllerHomeComponent implements OnInit, OnChanges, OnDestroy {
    @Input() view: ControllerView = 'painel';
    @Output() update = new EventEmitter<any>();
    @Output() controllerChange = new EventEmitter<Controller>();

    controller: Controller;
    loadState: 'loading' | 'ready' | 'error' = 'loading';
    history: ControllerControlRow[] = [];
    historyState: 'loading' | 'ready' | 'error' = 'loading';
    private controllerId: string;
    private destroy$ = new Subject<void>();
    filteredProducers$: Observable<any[]>;
    producerSearchCtrl = new FormControl('');
    selectedProducers: any[] = [];
    private selectedProducers$ = new BehaviorSubject<any[]>([]);

    constructor(
        private changeDetector: ChangeDetectorRef,
        private controllerRepository: ControllersApiRepository,
        private current: CurrentInfoService,
        private milkControls: MilkControlsApiRepository,
        private snackbar: MatSnackBar,
        private dialog: MatDialog
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['view'] && this.view === 'historico') {
            this.loadHistory();
        }
    }

    ngOnInit(): void {
        this.current.$value.pipe(
            map(info => info.user),
            filter((user): user is ControllerUser =>
                user instanceof ControllerUser && !!user.controller?.id
            ),
            map(user => user.controller.id),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(id => {
            this.controllerId = id;
            this.loadController();
        });

        this.filteredProducers$ = combineLatest([
            this.producerSearchCtrl.valueChanges.pipe(startWith(''), debounceTime(300)),
            this.selectedProducers$
        ]).pipe(
            map(([value, selected]) => {
                const searchTerm = (typeof value === 'string' ? value : '').toLowerCase();
                return (this.controller?.producers ?? [])
                    .filter(p =>
                        p.name.toLowerCase().includes(searchTerm) ||
                        p.code.toLowerCase().includes(searchTerm)
                    )
                    .filter(p => !selected.some(s => s.id === p.id));
            })
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.controller = null;
    }

    loadController(): void {
        if (!this.controllerId) return;

        this.loadState = 'loading';
        this.changeDetector.markForCheck();

        this.controllerRepository.find(this.controllerId).pipe(
            timeout(10000),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (controller) => {
                this.controller = controller;
                this.selectedProducers = [...controller.activeProducers];
                this.selectedProducers$.next(this.selectedProducers);
                this.loadState = 'ready';
                this.controllerChange.emit(controller);
                if (this.view === 'historico') this.loadHistory();
                this.changeDetector.markForCheck();
            },
            error: () => {
                this.loadState = 'error';
                this.changeDetector.markForCheck();
            }
        });
    }

    loadHistory(): void {
        if (!this.controller) return;

        this.historyState = 'loading';
        this.changeDetector.markForCheck();

        this.milkControls.workByController(this.controller.id, 40).pipe(
            timeout(10000),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (work) => {
                this.history = work?.controls ?? [];
                this.historyState = 'ready';
                this.changeDetector.markForCheck();
            },
            error: () => {
                this.historyState = 'error';
                this.changeDetector.markForCheck();
            }
        });
    }

    onOpen(row: ControllerControlRow): void {
        if (!row?.id) return;
        this.dialog.open(ProducerDetailsMilkControlAnimalsDialogComponent, {
            ...CONTROL_DRAWER_CONFIG,
            data: {
                summary: MilkControlSummary.fromControlLike(row),
                producer: new Producer(row.producerId || '', row.producerName || '', row.producerCode || '')
            }
        });
    }

    syncText(sync: string): string {
        if (sync === 'OK') return 'Enviado';
        if (sync === 'FAIL') return 'Falhou';
        if (sync === 'PENDING') return 'Na fila';
        return 'Sem envio';
    }

    formatDay(day?: string | null): string {
        const parts = (day || '').split('-');
        return parts.length === 3 && parts[2] ? `${parts[2]}/${parts[1]}/${parts[0]}` : '—';
    }

    onProducerSelected(event: MatAutocompleteSelectedEvent): void {
        const producer: Producer = event.option.value;

        if (!producer) return;

        if (!this.hasProducer(producer.id)) {
            this.updateSelectedProducers([...this.selectedProducers, producer]);
        }

        this.producerSearchCtrl.setValue(null);
        this.changeDetector.markForCheck();
    }

    onProducerRemoved(producer: ProducerSummary): void {
        this.updateSelectedProducers(this.selectedProducers.filter((p) => p.id !== producer.id));
    }

    private hasProducer(id: string): boolean {
        return this.selectedProducers.some((p) => p.id === id);
    }

    private async updateSelectedProducers(updatedList: Producer[]): Promise<void> {
        this.selectedProducers = updatedList;
        this.selectedProducers$.next(this.selectedProducers);

        await this.emitUpdate();
    }

    private async emitUpdate(): Promise<void> {
        this.controller.activeProducers = [...this.selectedProducers];

        try {
            const saved = await firstValueFrom(this.controllerRepository.update(this.controller).pipe(timeout(15000)));

            this.controller = saved;
            this.update.emit(saved);
            this.changeDetector.markForCheck();
        } catch (err) {
            this.snackbar.open('Falha ao salvar as alterações!', 'Fechar', { duration: 5000 });
            console.error('Erro ao salvar controller', err);
        }
    }
}
